/**
 * FullStory integration for the React app.
 *
 * Why: we need session replay for production debugging. Sentry breadcrumbs
 * only describe what the SDK observed; FullStory shows what the user actually
 * saw and clicked. Sentry MEITRE-FRONTEND-V2-BF investigation in 2026-05
 * stalled because we had no way to see what 87 users actually did before
 * hitting the /payment guard. With this module wired up, every Sentry event
 * for a known anomaly gains a one-click link to the session video.
 *
 * The legacy AngularJS app shipped FullStory but the React rewrite never
 * ported it. Production target org is `8F8EJ` (the "Meitre Frontend" org
 * from the legacy app — see /Volumes/Projects/meitre-frontend/src/index.
 * html line 101). NOT `o-224SKP-na1`, which is Ritual's separate org.
 *
 * Quota management is a project-level concern in FullStory's dashboard, not
 * here. This module loads and reports; project sampling controls how much
 * gets ingested.
 *
 * No-op contract: every public function is safe to call before `init` runs
 * or when `init` was skipped (no org ID env var, blocked by content blocker,
 * etc.). Failure to record is never allowed to break the UI.
 */

const FS_NAMESPACE = 'FS';
const FS_SCRIPT_ID = 'fullstory-script';

// Loose typing for the FS global. The real shape is FullStory's SDK v2 API.
// We never depend on the full surface — only the methods we explicitly use.
interface FullStoryApi {
  (action: 'event', payload: { name: string; properties?: Record<string, unknown> }): void;
  (action: 'identify', payload: { uid: string; properties?: Record<string, unknown> }): void;
  (action: 'getSession', options?: { format?: 'url' }): string | undefined;
  identify?: (uid: string, properties?: Record<string, unknown>) => void;
  event?: (name: string, properties?: Record<string, unknown>) => void;
  getCurrentSessionURL?: (now?: boolean) => string | undefined;
  shutdown?: () => void;
  restart?: () => void;
}

declare global {
  interface Window {
    FS?: FullStoryApi;
    _fs_namespace?: string;
    _fs_org?: string;
    _fs_script?: string;
    _fs_host?: string;
  }
}

/**
 * Inject the FullStory snippet. Idempotent — calling twice is a no-op.
 *
 * Should be called once from `main.tsx` after Sentry init. Pass the org ID
 * from `import.meta.env.VITE_FULLSTORY_ORG_ID`; if it's empty/undefined the
 * function returns early and no script is injected (dev / preview / opted-out
 * environments stay clean).
 */
export function initFullStory(orgId: string | undefined): void {
  if (!orgId) return;
  if (typeof window === 'undefined' || typeof document === 'undefined') return;
  // The guarantee-adjustment pay link's path is the token that reads the booking: a recording would keep it.
  if (window.location.pathname.startsWith('/guarantee-adjustment/')) return;
  if (document.getElementById(FS_SCRIPT_ID)) return;

  // Configure the snippet's expected globals BEFORE injecting the script.
  // Standard FullStory v2 snippet pattern: the loader reads these to wire up
  // the namespace, host, and src URL.
  window._fs_host = 'fullstory.com';
  window._fs_script = 'edge.fullstory.com/s/fs.js';
  window._fs_org = orgId;
  window._fs_namespace = FS_NAMESPACE;

  // Inject the standard FullStory v2 boot snippet. This is the minified
  // installer published by FullStory; it constructs a queueing API at
  // `window[FS_NAMESPACE]` and asynchronously loads the real SDK from
  // `window._fs_script`. Copy preserved verbatim from FullStory's docs so it
  // stays compatible with their SDK updates.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const m = window as any;
  const e = FS_NAMESPACE;
  if (e in m) {
    // Namespace conflict — bail rather than clobber. Will surface as a
    // console warning from FullStory's loader if this ever happens.
    if (m.console && m.console.log) {
      m.console.log('FullStory namespace conflict. Please set window["_fs_namespace"].');
    }
    return;
  }
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const g = (m[e] = function (...args: any[]) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const fn = m[e] as any;
    if (fn._api) return fn._api(...args);
    fn.q = fn.q || [];
    fn.q.push(args);
  });
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (g as any).q = [];

  const script = document.createElement('script');
  script.id = FS_SCRIPT_ID;
  script.async = true;
  script.crossOrigin = 'anonymous';
  script.src = `https://${window._fs_script}`;
  const firstScript = document.getElementsByTagName('script')[0];
  if (firstScript && firstScript.parentNode) {
    firstScript.parentNode.insertBefore(script, firstScript);
  } else {
    document.head.appendChild(script);
  }
}

/**
 * Tag the current session with the authenticated user's identity. Lets us
 * search FullStory by user ID and correlates sessions to a Meitre account.
 *
 * Safe to call before the SDK loads — calls queue.
 */
export function identifyFullStoryUser(
  userId: string,
  properties?: Record<string, unknown>,
): void {
  if (typeof window === 'undefined') return;
  const fs = window.FS;
  if (!fs) return;
  try {
    // FS v2 API — `FS('identify', { uid, properties })` is the documented form.
    fs('identify', { uid: userId, properties });
  } catch {
    // Never throw — analytics failure must not break the UI.
  }
}

/**
 * Mark a notable moment in the session. Use for anomalies you'd later want
 * to filter on in FullStory's UI (e.g. `payment_guard_fired`,
 * `commit_rejected`). The corresponding Sentry event should also include
 * the session replay URL via `getFullStoryReplayUrl()` so investigators can
 * jump straight from a Sentry event to the matching session video.
 */
export function markFullStoryEvent(
  name: string,
  properties?: Record<string, unknown>,
): void {
  if (typeof window === 'undefined') return;
  const fs = window.FS;
  if (!fs) return;
  try {
    fs('event', { name, properties });
  } catch {
    // Never throw — analytics failure must not break the UI.
  }
}

/**
 * Return a deep-link URL to the current FullStory session. Null when:
 *  - FullStory not loaded (env var unset, blocked, network failure)
 *  - Session not yet established (called too early in page lifecycle)
 *  - SDK threw
 *
 * Pass `now=true` to anchor the link at the current playhead position
 * (useful when paired with an error capture so the playback jumps to the
 * moment the error fired).
 */
export function getFullStoryReplayUrl(now: boolean = true): string | null {
  if (typeof window === 'undefined') return null;
  const fs = window.FS;
  if (!fs) return null;
  try {
    // FS v2 API: `FS('getSession', { format: 'url' })` returns the URL.
    // The older `FS.getCurrentSessionURL(now)` shim is still supported by
    // the snippet for compatibility — prefer it because it accepts the
    // `now` arg directly.
    if (typeof fs.getCurrentSessionURL === 'function') {
      return fs.getCurrentSessionURL(now) || null;
    }
    return fs('getSession', { format: 'url' }) || null;
  } catch {
    return null;
  }
}
